import { useCentralizedMe } from '@crehana/auth';
import { format } from '@crehana/date/format';
import { LANGUAGES } from '@crehana/ts-types';

import type { TCreateRequestPayload } from '../../components/create-request-dialog';
import { useTimeAndAttendanceUserOvertimeRequestCreateMutation } from './create-overtime-request.v5.generated';

type TCreateOvertimeRequestStatus =
  | 'success'
  | 'duplicate'
  | 'shiftRequired'
  | 'error';

const DUPLICATE_CODES: ReadonlySet<string> = new Set([
  'OVERTIME_REQUEST_ALREADY_EXISTS',
  'OVERTIME_REQUEST_DAY_ALREADY_CLASSIFIED',
]);
const SHIFT_REQUIRED_CODE = 'OVERTIME_REQUEST_SHIFT_REQUIRED';

export const useCreateOvertimeRequest = () => {
  const { currentPageOrganization } = useCentralizedMe();
  const orgId = currentPageOrganization?.id ?? 0;

  const [createMutation, { loading: creating }] =
    useTimeAndAttendanceUserOvertimeRequestCreateMutation();

  const createRequest = async (
    payload: TCreateRequestPayload,
  ): Promise<TCreateOvertimeRequestStatus> => {
    try {
      const { data } = await createMutation({
        variables: {
          input: {
            centralized_organization_id: orgId,
            request_date: format({
              date: payload.date,
              pattern: 'yyyy-MM-dd',
              languageCode: LANGUAGES.EN,
            }),
            // TIM-663 CAMBIO A — a request never sends null while a turno
            // exists: `shiftId` is null only when the day itself has zero
            // turnos, a state that already blocks Guardar client-side.
            shift_id: payload.shiftId,
            extra_minutes: payload.extraMinutes,
            replacement_minutes: payload.replacementMinutes,
            reason: payload.reason,
          },
        },
        context: { clientName: 'v5' },
      });
      const result = data?.time.overtime_requests.create_overtime_request;

      if (result?.__typename === 'TimeCreateOvertimeRequestPayload') {
        return 'success';
      }
      if (result?.__typename === 'IntegrityErrorResponse') {
        return 'duplicate';
      }
      if (result?.__typename === 'ValidationErrorResponse') {
        if (DUPLICATE_CODES.has(result.code)) {
          return 'duplicate';
        }
        if (result.code === SHIFT_REQUIRED_CODE) {
          return 'shiftRequired';
        }
      }
      return 'error';
    } catch {
      return 'error';
    }
  };

  return { createRequest, creating };
};
